import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import AdminLayout from '../../components/AdminLayout';
import {
  FiUsers, FiPlus, FiSearch, FiRefreshCw, FiEye, FiDownload, FiEdit, FiTrash2,
  FiDollarSign, FiTrendingUp, FiPercent, FiCreditCard, FiExternalLink,
  FiX, FiCopy, FiCheck, FiBarChart2, FiCalendar, FiTarget, FiActivity,
  FiImage, FiLink, FiLock, FiChevronUp, FiChevronDown, FiUser
} from 'react-icons/fi';

// Import shared admin components
import {
  AdminContainer, AdminHeader, HeaderContent, HeaderText, HeaderActions,
  ActionButton, MainContent, StatsGrid, StatCard, StatHeader, StatIcon,
  StatValue, StatLabel, SearchAndFilters, SearchBox, FilterSelect,
  ActionButtonGroup, DataTable, TableActionButton, Modal, ModalContent,
  ModalActions, ResultsCounter, LoadingSpinner, ErrorMessage
} from '../../styles/AdminComponents';

// Modal form specific styled components
import styled from 'styled-components';

const ModalFormGroup = styled.div`
  margin-bottom: 1rem;
  
  label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
    color: #374151;
  }
  
  input, select, textarea {
    width: 100%;
    padding: 0.75rem;
    border: 1px solid #d1d5db;
    border-radius: 0.5rem;
    font-size: 1rem;
    
    &:focus {
      outline: none;
      border-color: #3b82f6;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }
  }
  
  textarea {
    resize: vertical;
    min-height: 100px;
  }
`;

// Enhanced Mobile-responsive referrer card
const ReferrerCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  
  .referrer-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 1rem;
    gap: 1rem;
    
    .referrer-info {
      flex: 1;
      min-width: 0;
      display: flex;
      align-items: center;
      gap: 1rem;
      
      .logo {
        width: 48px;
        height: 48px;
        border-radius: 50%;
        background: #f3f4f6;
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
        flex-shrink: 0;
        
        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
      }
      
      .details {
        flex: 1;
        min-width: 0;
        
        .referrer-name {
          font-size: 1.1rem;
          font-weight: 600;
          color: #111827;
          margin-bottom: 0.25rem;
        }
        
        .referrer-id {
          font-size: 0.8rem;
          font-family: 'Monaco', 'Menlo', monospace;
          background: #f3f4f6;
          padding: 0.25rem 0.5rem;
          border-radius: 0.25rem;
          color: #6b7280;
          display: inline-block;
        }
      }
    }
    
    .actions {
      display: flex;
      gap: 0.5rem;
      flex-shrink: 0;
      flex-wrap: wrap;
    }
  }
  
  .referrer-details {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
    margin-bottom: 1rem;
    
    @media (max-width: 480px) {
      grid-template-columns: 1fr;
      gap: 0.75rem;
    }
    
    .detail-item {
      .label {
        font-size: 0.75rem;
        font-weight: 600;
        color: #6b7280;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        margin-bottom: 0.25rem;
      }
      
      .value {
        font-size: 0.9rem;
        color: #111827;
        font-weight: 500;
      }
    }
  }
  
  .enhanced-details {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
    gap: 1rem;
    padding-top: 1rem;
    border-top: 1px solid #e5e7eb;
    
    .stat-item {
      text-align: center;
      
      .stat-label {
        font-size: 0.7rem;
        color: #6b7280;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        margin-bottom: 0.25rem;
      }
      
      .stat-value {
        font-size: 1rem;
        font-weight: 700;
        color: #111827;
      }
    }
  }
`;

// Responsive container that switches between table and cards
const ResponsiveContainer = styled.div`
  .desktop-view {
    display: block;
    
    @media (max-width: 768px) {
      display: none;
    }
  }
  
  .mobile-view {
    display: none;
    
    @media (max-width: 768px) {
      display: block;
    }
  }
`;

// Analytics Modal styled components
const AnalyticsModal = styled(Modal)`
  .modal-content {
    max-width: 900px;
    max-height: 90vh;
    overflow-y: auto;
  }
`;

const AnalyticsSection = styled.div`
  margin-bottom: 2rem;
  
  h3 {
    font-size: 1.1rem;
    font-weight: 600;
    color: #111827;
    margin-bottom: 1rem;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid #e5e7eb;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
`;

const TimeStatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 0.75rem;
  animation: fadeIn 0.3s ease-in;
  
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-10px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const TimeStatCard = styled.div`
  padding: 0.75rem;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  text-align: center;
  background: ${props => props.hasActivity ? '#f8f9fa' : '#fff'};
  transition: all 0.2s ease;
  font-size: 0.85rem;
  
  .period {
    font-size: 0.8rem;
    font-weight: bold;
    color: #495057;
    margin-bottom: 0.4rem;
    line-height: 1.2;
    height: 2.4em;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .count {
    font-size: 1.3rem;
    font-weight: bold;
    color: ${props => props.hasActivity ? '#28a745' : '#6c757d'};
    margin-bottom: 0.2rem;
  }
  
  .orders-label {
    font-size: 0.7rem;
    color: #6c757d;
    margin-bottom: 0.3rem;
  }
  
  .revenue {
    font-size: 1rem;
    font-weight: bold;
    color: ${props => props.revenue > 0 ? '#007bff' : '#6c757d'};
  }
`;

const ReferrersPage = () => {
  const [referrers, setReferrers] = useState([]);
  const [filteredReferrers, setFilteredReferrers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('total_revenue');
  const [sortDirection, setSortDirection] = useState('desc');
  const [referrerStats, setReferrerStats] = useState(null);

  // Modal states
  const [showReferrerModal, setShowReferrerModal] = useState(false);
  const [editingReferrer, setEditingReferrer] = useState(null);
  const [referrerForm, setReferrerForm] = useState({
    id: '', name: '', logo_url: '', channel: '', description: '', password: '', percent: ''
  });

  // Analytics modal states
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedReferrerDetails, setSelectedReferrerDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [showMonthlyStats, setShowMonthlyStats] = useState(false);
  const [selectedTimeframe, setSelectedTimeframe] = useState('last_month');

  const adminRank = localStorage.getItem('adminRank');

  useEffect(() => {
    if (adminRank === 'owner') {
      fetchReferrers();
    }
  }, [adminRank]);

  useEffect(() => {
    const filtered = referrers.filter(referrer => {
      const matchesSearch = !searchTerm || 
        referrer.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        referrer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (referrer.channel && referrer.channel.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (referrer.description && referrer.description.toLowerCase().includes(searchTerm.toLowerCase()));

      return matchesSearch;
    });

    setFilteredReferrers(filtered);
  }, [referrers, searchTerm]);

  const fetchReferrers = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const headers = { Authorization: `Bearer ${token}` };
      
      const res = await axios.get('/api/admin/referrers', { headers });
      
      if (res.data.success) {
        setReferrers(res.data.referrers || []);
        
        // Calculate comprehensive referrer stats
        const referrersData = res.data.referrers || [];
        const stats = {
          total_referrers: referrersData.length,
          active_referrers: referrersData.filter(r => r.total_payments > 0).length,
          total_orders: referrersData.reduce((sum, r) => sum + (r.total_payments || 0), 0),
          total_revenue: referrersData.reduce((sum, r) => sum + (r.total_revenue || 0), 0),
          total_commissions: referrersData.reduce((sum, r) => sum + (r.referrer_earnings || 0), 0),
          avg_commission_rate: referrersData.length > 0 
            ? referrersData.reduce((sum, r) => sum + (r.percent || 0), 0) / referrersData.length 
            : 0,
          top_performer: referrersData.reduce((top, current) => 
            (current.total_revenue || 0) > (top.total_revenue || 0) ? current : top, 
            referrersData[0] || {}
          )
        };
        setReferrerStats(stats);
      }
      
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch referrers');
      setLoading(false);
      toast.error('Failed to fetch referrers');
    }
  };

  const refreshReferrers = () => {
    fetchReferrers();
  };

  const exportReferrers = () => {
    const csvContent = [
      ['ID', 'Name', 'Channel', 'Commission %', 'Total Orders', 'Total Revenue', 'Earnings', 'Last Sale'],
      ...filteredReferrers.map(referrer => [
        referrer.id,
        referrer.name,
        referrer.channel || '',
        referrer.percent || 0,
        referrer.total_payments || 0,
        referrer.total_revenue || 0,
        referrer.referrer_earnings || 0,
        referrer.last_payment_date || 'Never'
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'referrers_export.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  // Sort referrers
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const sortedReferrers = [...filteredReferrers].sort((a, b) => {
    let aVal = a[sortField];
    let bVal = b[sortField];
    
    if (sortField === 'last_payment_date') {
      aVal = aVal ? new Date(aVal) : new Date(0);
      bVal = bVal ? new Date(bVal) : new Date(0);
    } else if (typeof aVal === 'string') {
      aVal = aVal.toLowerCase();
      bVal = bVal.toLowerCase();
    } else {
      aVal = aVal || 0;
      bVal = bVal || 0;
    }
    
    if (sortDirection === 'asc') {
      return aVal > bVal ? 1 : -1;
    } else {
      return aVal < bVal ? 1 : -1;
    }
  });

  // Referrer form handlers
  const handleReferrerFormChange = (e) => {
    setReferrerForm({ ...referrerForm, [e.target.name]: e.target.value });
  };

  const handleReferrerSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('adminToken');
      
      if (editingReferrer) {
        await axios.put(`/api/admin/referrers/${editingReferrer.id}`, referrerForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Referrer updated successfully!');
      } else {
        await axios.post('/api/admin/referrers', referrerForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Referrer created successfully!');
      }
      
      setShowReferrerModal(false);
      setEditingReferrer(null);
      setReferrerForm({ id: '', name: '', logo_url: '', channel: '', description: '', password: '', percent: '' });
      fetchReferrers();
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Failed to save referrer';
      toast.error(errorMsg);
    }
  };

  const handleEditReferrer = (referrer) => {
    setEditingReferrer(referrer);
    setReferrerForm(referrer);
    setShowReferrerModal(true);
  };

  const handleDeleteReferrer = async (id) => {
    if (!window.confirm('Are you sure you want to delete this referrer?')) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`/api/admin/referrers/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Referrer deleted successfully!');
      fetchReferrers();
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Failed to delete referrer';
      toast.error(errorMsg);
    }
  };

  // Analytics handlers
  const handleShowDetails = async (referrer) => {
    setLoadingDetails(true);
    setSelectedReferrerDetails(referrer);
    setShowDetailsModal(true);
    await loadReferrerDetails(referrer.id);
  };

  const loadReferrerDetails = async (referrerId, timeframe = selectedTimeframe) => {
    setLoadingDetails(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.get(`/api/admin/referrers/${referrerId}/details`, { 
        headers: { Authorization: `Bearer ${token}` },
        params: { timeframe }
      });
      
      if (res.data.success) {
        setSelectedReferrerDetails(prev => ({
          ...prev,
          detailedPayments: res.data.payments || [],
          planBreakdown: res.data.planBreakdown || {},
          timeStats: res.data.timeStats || [],
          timeframeName: res.data.timeframeName || 'Recent'
        }));
      }
    } catch (err) {
      toast.error('Failed to load detailed information');
      console.error('Error loading referrer details:', err);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleTimeframeChange = (newTimeframe) => {
    setSelectedTimeframe(newTimeframe);
    if (selectedReferrerDetails) {
      loadReferrerDetails(selectedReferrerDetails.id, newTimeframe);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString();
  };

  if (adminRank !== 'owner') {
    return (
      <AdminLayout>
        <AdminContainer>
          <AdminHeader>
            <HeaderContent>
              <HeaderText>
                <h1>Access Denied</h1>
                <p>Only owners can manage referrers</p>
              </HeaderText>
            </HeaderContent>
          </AdminHeader>
        </AdminContainer>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <AdminContainer>
        <AdminHeader>
          <HeaderContent>
            <HeaderText>
              <h1>Referrer Management</h1>
              <p>Manage referral partners and track performance</p>
            </HeaderText>
            <HeaderActions>
              <ActionButton onClick={() => {
                setShowReferrerModal(true);
                setEditingReferrer(null);
                setReferrerForm({ id: '', name: '', logo_url: '', channel: '', description: '', password: '', percent: '' });
              }}>
                <FiPlus />
                <span className="btn-text">Add Referrer</span>
              </ActionButton>
              <ActionButton onClick={refreshReferrers} variant="secondary">
                <FiRefreshCw />
                <span className="btn-text">Refresh</span>
              </ActionButton>
              <ActionButton onClick={exportReferrers} variant="secondary">
                <FiDownload />
                <span className="btn-text">Export CSV</span>
              </ActionButton>
            </HeaderActions>
          </HeaderContent>
        </AdminHeader>

        <MainContent>
          {/* Enhanced Referrer Stats Section */}
          {referrerStats && (
            <StatsGrid>
              <StatCard>
                <StatHeader>
                  <StatIcon><FiUsers color="#3b82f6" /></StatIcon>
                  <h3>Total Referrers</h3>
                </StatHeader>
                <StatValue>{referrerStats.total_referrers}</StatValue>
                <StatLabel>Registered Partners</StatLabel>
              </StatCard>
              <StatCard>
                <StatHeader>
                  <StatIcon><FiActivity color="#10b981" /></StatIcon>
                  <h3>Active Referrers</h3>
                </StatHeader>
                <StatValue>{referrerStats.active_referrers}</StatValue>
                <StatLabel>With Sales</StatLabel>
              </StatCard>
              <StatCard>
                <StatHeader>
                  <StatIcon><FiCreditCard color="#8b5cf6" /></StatIcon>
                  <h3>Total Orders</h3>
                </StatHeader>
                <StatValue>{referrerStats.total_orders}</StatValue>
                <StatLabel>Referred Sales</StatLabel>
              </StatCard>
              <StatCard>
                <StatHeader>
                  <StatIcon><FiDollarSign color="#10b981" /></StatIcon>
                  <h3>Total Revenue</h3>
                </StatHeader>
                <StatValue>{formatCurrency(referrerStats.total_revenue)}</StatValue>
                <StatLabel>Generated Sales</StatLabel>
              </StatCard>
              <StatCard>
                <StatHeader>
                  <StatIcon><FiTrendingUp color="#f59e0b" /></StatIcon>
                  <h3>Total Commissions</h3>
                </StatHeader>
                <StatValue>{formatCurrency(referrerStats.total_commissions)}</StatValue>
                <StatLabel>Earned by Partners</StatLabel>
              </StatCard>
              <StatCard>
                <StatHeader>
                  <StatIcon><FiPercent color="#06b6d4" /></StatIcon>
                  <h3>Avg Commission</h3>
                </StatHeader>
                <StatValue>{referrerStats.avg_commission_rate.toFixed(1)}%</StatValue>
                <StatLabel>Rate Average</StatLabel>
              </StatCard>
              <StatCard>
                <StatHeader>
                  <StatIcon><FiTarget color="#8b5cf6" /></StatIcon>
                  <h3>Top Performer</h3>
                </StatHeader>
                <StatValue style={{ fontSize: '1.5rem' }}>
                  {referrerStats.top_performer?.name || 'None'}
                </StatValue>
                <StatLabel>{formatCurrency(referrerStats.top_performer?.total_revenue || 0)}</StatLabel>
              </StatCard>
              <StatCard>
                <StatHeader>
                  <StatIcon><FiCalendar color="#14b8a6" /></StatIcon>
                  <h3>Performance</h3>
                </StatHeader>
                <StatValue>
                  {referrerStats.active_referrers > 0 
                    ? (referrerStats.total_revenue / referrerStats.active_referrers).toFixed(0)
                    : '0'
                  }
                </StatValue>
                <StatLabel>Avg Revenue/Partner</StatLabel>
              </StatCard>
            </StatsGrid>
          )}

          <SearchAndFilters>
            <SearchBox>
              <FiSearch />
              <input
                type="text"
                placeholder="Search referrers (ID, name, channel, description)..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </SearchBox>
          </SearchAndFilters>

          <ActionButtonGroup>
            <ResultsCounter>{sortedReferrers.length} referrers found</ResultsCounter>
          </ActionButtonGroup>

          {loading ? (
            <LoadingSpinner />
          ) : error ? (
            <ErrorMessage>{error}</ErrorMessage>
          ) : (
            <ResponsiveContainer>
              {/* Desktop Table View */}
              <div className="desktop-view">
                <DataTable>
                  <thead>
                    <tr>
                      <th onClick={() => handleSort('id')} style={{ cursor: 'pointer' }}>
                        ID {sortField === 'id' && (sortDirection === 'asc' ? <FiChevronUp /> : <FiChevronDown />)}
                      </th>
                      <th onClick={() => handleSort('name')} style={{ cursor: 'pointer' }}>
                        Name {sortField === 'name' && (sortDirection === 'asc' ? <FiChevronUp /> : <FiChevronDown />)}
                      </th>
                      <th>Logo</th>
                      <th onClick={() => handleSort('channel')} style={{ cursor: 'pointer' }}>
                        Channel {sortField === 'channel' && (sortDirection === 'asc' ? <FiChevronUp /> : <FiChevronDown />)}
                      </th>
                      <th onClick={() => handleSort('percent')} style={{ cursor: 'pointer' }}>
                        Commission % {sortField === 'percent' && (sortDirection === 'asc' ? <FiChevronUp /> : <FiChevronDown />)}
                      </th>
                      <th onClick={() => handleSort('total_payments')} style={{ cursor: 'pointer' }}>
                        Orders {sortField === 'total_payments' && (sortDirection === 'asc' ? <FiChevronUp /> : <FiChevronDown />)}
                      </th>
                      <th onClick={() => handleSort('total_revenue')} style={{ cursor: 'pointer' }}>
                        Revenue {sortField === 'total_revenue' && (sortDirection === 'asc' ? <FiChevronUp /> : <FiChevronDown />)}
                      </th>
                      <th onClick={() => handleSort('referrer_earnings')} style={{ cursor: 'pointer' }}>
                        Earnings {sortField === 'referrer_earnings' && (sortDirection === 'asc' ? <FiChevronUp /> : <FiChevronDown />)}
                      </th>
                      <th onClick={() => handleSort('last_payment_date')} style={{ cursor: 'pointer' }}>
                        Last Sale {sortField === 'last_payment_date' && (sortDirection === 'asc' ? <FiChevronUp /> : <FiChevronDown />)}
                      </th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedReferrers.map((referrer, index) => (
                      <tr key={referrer.id || index}>
                        <td>
                          <code className="code-cell">{referrer.id}</code>
                        </td>
                        <td style={{ fontWeight: '500' }}>{referrer.name}</td>
                        <td>
                          {referrer.logo_url ? (
                            <img 
                              src={referrer.logo_url} 
                              alt="logo" 
                              style={{ 
                                width: '32px', 
                                height: '32px', 
                                borderRadius: '50%', 
                                objectFit: 'cover' 
                              }} 
                            />
                          ) : (
                            <div style={{
                              width: '32px',
                              height: '32px',
                              borderRadius: '50%',
                              background: '#f3f4f6',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <FiImage color="#9ca3af" />
                            </div>
                          )}
                        </td>
                        <td>{referrer.channel || '-'}</td>
                        <td>
                          <span style={{
                            padding: '4px 8px',
                            borderRadius: '12px',
                            fontSize: '0.8em',
                            fontWeight: '500',
                            background: '#dbeafe',
                            color: '#1d4ed8'
                          }}>
                            {referrer.percent}%
                          </span>
                        </td>
                        <td>
                          <span style={{ 
                            fontWeight: 'bold', 
                            color: (referrer.total_payments || 0) > 0 ? '#10b981' : '#6b7280' 
                          }}>
                            {referrer.total_payments || 0}
                          </span>
                        </td>
                        <td>
                          <span style={{ 
                            fontWeight: 'bold', 
                            color: (referrer.total_revenue || 0) > 0 ? '#10b981' : '#6b7280' 
                          }}>
                            {formatCurrency(referrer.total_revenue)}
                          </span>
                        </td>
                        <td>
                          <span style={{ 
                            fontWeight: 'bold', 
                            color: (referrer.referrer_earnings || 0) > 0 ? '#3b82f6' : '#6b7280' 
                          }}>
                            {formatCurrency(referrer.referrer_earnings)}
                          </span>
                        </td>
                        <td>{formatDate(referrer.last_payment_date)}</td>
                        <td>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <TableActionButton 
                              onClick={() => handleShowDetails(referrer)}
                              title="View Analytics"
                            >
                              <FiBarChart2 />
                            </TableActionButton>
                            <TableActionButton 
                              onClick={() => window.open(`/ref/${referrer.id}`, '_blank')}
                              title="View Panel"
                            >
                              <FiExternalLink />
                            </TableActionButton>
                            <TableActionButton 
                              onClick={() => handleEditReferrer(referrer)}
                              title="Edit Referrer"
                            >
                              <FiEdit />
                            </TableActionButton>
                            <TableActionButton 
                              onClick={() => handleDeleteReferrer(referrer.id)}
                              variant="danger"
                              title="Delete Referrer"
                            >
                              <FiTrash2 />
                            </TableActionButton>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </DataTable>
              </div>

              {/* Enhanced Mobile Card View */}
              <div className="mobile-view">
                {sortedReferrers.map((referrer, index) => (
                  <ReferrerCard key={referrer.id || index}>
                    <div className="referrer-header">
                      <div className="referrer-info">
                        <div className="logo">
                          {referrer.logo_url ? (
                            <img src={referrer.logo_url} alt="logo" />
                          ) : (
                            <FiImage color="#9ca3af" />
                          )}
                        </div>
                        <div className="details">
                          <div className="referrer-name">{referrer.name}</div>
                          <div className="referrer-id">{referrer.id}</div>
                        </div>
                      </div>
                      <div className="actions">
                        <TableActionButton 
                          onClick={() => handleShowDetails(referrer)}
                          title="View Analytics"
                        >
                          <FiBarChart2 />
                        </TableActionButton>
                        <TableActionButton 
                          onClick={() => window.open(`/ref/${referrer.id}`, '_blank')}
                          title="View Panel"
                        >
                          <FiExternalLink />
                        </TableActionButton>
                        <TableActionButton 
                          onClick={() => handleEditReferrer(referrer)}
                          title="Edit"
                        >
                          <FiEdit />
                        </TableActionButton>
                        <TableActionButton 
                          onClick={() => handleDeleteReferrer(referrer.id)}
                          variant="danger"
                          title="Delete"
                        >
                          <FiTrash2 />
                        </TableActionButton>
                      </div>
                    </div>
                    
                    <div className="referrer-details">
                      <div className="detail-item">
                        <div className="label">Channel</div>
                        <div className="value">{referrer.channel || 'Not specified'}</div>
                      </div>
                      <div className="detail-item">
                        <div className="label">Commission</div>
                        <div className="value">{referrer.percent}%</div>
                      </div>
                      <div className="detail-item">
                        <div className="label">Orders</div>
                        <div className="value">{referrer.total_payments || 0}</div>
                      </div>
                      <div className="detail-item">
                        <div className="label">Last Sale</div>
                        <div className="value">{formatDate(referrer.last_payment_date)}</div>
                      </div>
                    </div>
                    
                    <div className="enhanced-details">
                      <div className="stat-item">
                        <div className="stat-label">Revenue</div>
                        <div className="stat-value">{formatCurrency(referrer.total_revenue)}</div>
                      </div>
                      <div className="stat-item">
                        <div className="stat-label">Earnings</div>
                        <div className="stat-value">{formatCurrency(referrer.referrer_earnings)}</div>
                      </div>
                      <div className="stat-item">
                        <div className="stat-label">Completed</div>
                        <div className="stat-value">{referrer.completed_payments || 0}</div>
                      </div>
                      <div className="stat-item">
                        <div className="stat-label">Pending</div>
                        <div className="stat-value">{referrer.pending_payments || 0}</div>
                      </div>
                    </div>
                  </ReferrerCard>
                ))}
              </div>
            </ResponsiveContainer>
          )}

          {/* Create/Edit Referrer Modal */}
          {showReferrerModal && (
            <Modal>
              <ModalContent>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                  <h2>{editingReferrer ? 'Edit Referrer' : '🎯 Create New Referrer'}</h2>
                  <button 
                    onClick={() => {
                      setShowReferrerModal(false);
                      setEditingReferrer(null);
                    }}
                    style={{ 
                      background: 'none', 
                      border: 'none', 
                      fontSize: '1.5rem', 
                      cursor: 'pointer',
                      color: '#6b7280',
                      padding: '0.5rem',
                      borderRadius: '0.25rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <FiX />
                  </button>
                </div>

                <form onSubmit={handleReferrerSubmit}>
                  <ModalFormGroup>
                    <label>
                      <FiLink style={{ marginRight: '0.5rem' }} />
                      Referrer ID (URL slug)
                    </label>
                    <input 
                      name="id" 
                      value={referrerForm.id} 
                      onChange={handleReferrerFormChange} 
                      required 
                      disabled={!!editingReferrer}
                      placeholder="e.g. youtuber123"
                    />
                    {!editingReferrer && (
                      <small style={{ color: '#6b7280', marginTop: '0.25rem' }}>
                        This will be used in URLs: /ref/{referrerForm.id}
                      </small>
                    )}
                  </ModalFormGroup>

                  <ModalFormGroup>
                    <label>
                      <FiUser style={{ marginRight: '0.5rem' }} />
                      Display Name
                    </label>
                    <input 
                      name="name" 
                      value={referrerForm.name} 
                      onChange={handleReferrerFormChange} 
                      required 
                      placeholder="e.g. TechReviewer Pro"
                    />
                  </ModalFormGroup>

                  <ModalFormGroup>
                    <label>
                      <FiImage style={{ marginRight: '0.5rem' }} />
                      Logo URL (optional)
                    </label>
                    <input 
                      name="logo_url" 
                      value={referrerForm.logo_url} 
                      onChange={handleReferrerFormChange} 
                      placeholder="https://example.com/logo.png"
                    />
                  </ModalFormGroup>

                  <ModalFormGroup>
                    <label>
                      <FiActivity style={{ marginRight: '0.5rem' }} />
                      Channel/Platform
                    </label>
                    <input 
                      name="channel" 
                      value={referrerForm.channel} 
                      onChange={handleReferrerFormChange}
                      placeholder="e.g. YouTube, Twitter, Discord"
                    />
                  </ModalFormGroup>

                  <ModalFormGroup>
                    <label>Description</label>
                    <textarea 
                      name="description" 
                      value={referrerForm.description} 
                      onChange={handleReferrerFormChange}
                      placeholder="Brief description of the referrer or partnership"
                    />
                  </ModalFormGroup>

                  <ModalFormGroup>
                    <label>
                      <FiLock style={{ marginRight: '0.5rem' }} />
                      Password
                    </label>
                    <input 
                      name="password" 
                      type="password" 
                      value={referrerForm.password || ''} 
                      onChange={handleReferrerFormChange} 
                      placeholder={editingReferrer ? "Leave blank to keep current password" : "Set password for referrer panel access"}
                    />
                  </ModalFormGroup>

                  <ModalFormGroup>
                    <label>
                      <FiPercent style={{ marginRight: '0.5rem' }} />
                      Commission Percentage
                    </label>
                    <input 
                      name="percent" 
                      type="number" 
                      min="0" 
                      max="100" 
                      step="0.01" 
                      value={referrerForm.percent || ''} 
                      onChange={handleReferrerFormChange} 
                      placeholder="e.g. 20 for 20% commission"
                    />
                  </ModalFormGroup>

                  <ModalActions>
                    <ActionButton 
                      type="button" 
                      onClick={() => {
                        setShowReferrerModal(false);
                        setEditingReferrer(null);
                      }} 
                      variant="secondary"
                    >
                      Cancel
                    </ActionButton>
                    <ActionButton type="submit">
                      <FiPlus />
                      {editingReferrer ? 'Update Referrer' : 'Create Referrer'}
                    </ActionButton>
                  </ModalActions>
                </form>
              </ModalContent>
            </Modal>
          )}

          {/* Analytics Details Modal */}
          {showDetailsModal && selectedReferrerDetails && (
            <AnalyticsModal>
              <ModalContent className="modal-content">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    {selectedReferrerDetails.logo_url && (
                      <img 
                        src={selectedReferrerDetails.logo_url} 
                        alt="logo" 
                        style={{ width: '48px', height: '48px', borderRadius: '50%', objectFit: 'cover' }} 
                      />
                    )}
                    <h2>📊 {selectedReferrerDetails.name} Analytics</h2>
                  </div>
                  <button 
                    onClick={() => setShowDetailsModal(false)}
                    style={{ 
                      background: 'none', 
                      border: 'none', 
                      fontSize: '1.5rem', 
                      cursor: 'pointer',
                      color: '#6b7280',
                      padding: '0.5rem',
                      borderRadius: '0.25rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <FiX />
                  </button>
                </div>

                {loadingDetails ? (
                  <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                    <LoadingSpinner />
                    Loading detailed analytics...
                  </div>
                ) : (
                  <>
                    {/* Quick Stats */}
                    <AnalyticsSection>
                      <h3><FiBarChart2 /> Performance Overview</h3>
                      <StatsGrid style={{ marginBottom: 0 }}>
                        <StatCard style={{ margin: 0 }}>
                          <StatValue style={{ fontSize: '1.8rem' }}>
                            {selectedReferrerDetails.total_payments || 0}
                          </StatValue>
                          <StatLabel>Total Orders</StatLabel>
                        </StatCard>
                        <StatCard style={{ margin: 0 }}>
                          <StatValue style={{ fontSize: '1.8rem', color: '#10b981' }}>
                            {formatCurrency(selectedReferrerDetails.total_revenue)}
                          </StatValue>
                          <StatLabel>Total Revenue</StatLabel>
                        </StatCard>
                        <StatCard style={{ margin: 0 }}>
                          <StatValue style={{ fontSize: '1.8rem', color: '#3b82f6' }}>
                            {formatCurrency(selectedReferrerDetails.referrer_earnings)}
                          </StatValue>
                          <StatLabel>Earnings ({selectedReferrerDetails.percent}%)</StatLabel>
                        </StatCard>
                        <StatCard style={{ margin: 0 }}>
                          <StatValue style={{ fontSize: '1.8rem', color: '#f59e0b' }}>
                            {selectedReferrerDetails.completed_payments || 0}
                          </StatValue>
                          <StatLabel>Completed Orders</StatLabel>
                        </StatCard>
                      </StatsGrid>
                    </AnalyticsSection>

                    {/* Plan Breakdown */}
                    {selectedReferrerDetails.planBreakdown && Object.keys(selectedReferrerDetails.planBreakdown).length > 0 && (
                      <AnalyticsSection>
                        <h3><FiTarget /> Plan Breakdown</h3>
                        <div style={{ 
                          display: 'grid', 
                          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                          gap: '1rem'
                        }}>
                          {Object.entries(selectedReferrerDetails.planBreakdown).map(([plan, data]) => (
                            <div key={plan} style={{ 
                              padding: '1rem', 
                              border: '1px solid #e5e7eb', 
                              borderRadius: '8px',
                              textAlign: 'center',
                              background: '#f8fafc'
                            }}>
                              <div style={{ 
                                fontSize: '1.2rem', 
                                fontWeight: 'bold', 
                                textTransform: 'capitalize',
                                marginBottom: '0.5rem'
                              }}>
                                {plan}
                              </div>
                              <div style={{ color: '#10b981', fontWeight: 'bold', marginBottom: '0.25rem' }}>
                                {data.count} orders
                              </div>
                              <div style={{ color: '#3b82f6', fontWeight: 'bold' }}>
                                {formatCurrency(data.revenue)}
                              </div>
                            </div>
                          ))}
                        </div>
                      </AnalyticsSection>
                    )}

                    {/* Time Performance */}
                    <AnalyticsSection>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', flexWrap: 'wrap', gap: '1rem' }}>
                        <h3 style={{ margin: 0 }}>
                          <FiCalendar /> Performance: {selectedReferrerDetails.timeframeName || 'Recent'}
                        </h3>
                        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                          <FilterSelect
                            value={selectedTimeframe}
                            onChange={(e) => handleTimeframeChange(e.target.value)}
                          >
                            <option value="last_week">Last 7 Days</option>
                            <option value="last_month">Last 30 Days</option>
                            <option value="last_3_months">Last 3 Months</option>
                            <option value="last_6_months">Last 6 Months</option>
                            <option value="all_time">All Time</option>
                          </FilterSelect>
                          <ActionButton
                            onClick={() => setShowMonthlyStats(!showMonthlyStats)}
                            variant="secondary"
                          >
                            {showMonthlyStats ? <FiChevronUp /> : <FiChevronDown />}
                            {showMonthlyStats ? 'Hide Chart' : 'Show Chart'}
                          </ActionButton>
                        </div>
                      </div>
                      
                      {showMonthlyStats && selectedReferrerDetails.timeStats && selectedReferrerDetails.timeStats.length > 0 && (
                        <TimeStatsGrid>
                          {selectedReferrerDetails.timeStats.map((stat, index) => (
                            <TimeStatCard key={index} hasActivity={stat.count > 0} revenue={stat.revenue}>
                              <div className="period">{stat.period}</div>
                              <div className="count">{stat.count}</div>
                              <div className="orders-label">orders</div>
                              <div className="revenue">{formatCurrency(stat.revenue)}</div>
                            </TimeStatCard>
                          ))}
                        </TimeStatsGrid>
                      )}
                      
                      {showMonthlyStats && (!selectedReferrerDetails.timeStats || selectedReferrerDetails.timeStats.length === 0) && (
                        <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280', background: '#f8fafc', borderRadius: '8px' }}>
                          No data available for the selected timeframe.
                        </div>
                      )}
                    </AnalyticsSection>

                    {/* Recent Payments */}
                    {selectedReferrerDetails.detailedPayments && selectedReferrerDetails.detailedPayments.length > 0 && (
                      <AnalyticsSection>
                        <h3><FiCreditCard /> Recent Payments (Latest 20)</h3>
                        <div style={{ overflowX: 'auto' }}>
                          <table style={{ 
                            width: '100%', 
                            borderCollapse: 'collapse', 
                            minWidth: '700px',
                            fontSize: '0.85rem'
                          }}>
                            <thead>
                              <tr style={{ background: '#f8fafc' }}>
                                <th style={{ padding: '0.75rem', border: '1px solid #e5e7eb', textAlign: 'left' }}>Date</th>
                                <th style={{ padding: '0.75rem', border: '1px solid #e5e7eb', textAlign: 'left' }}>Amount</th>
                                <th style={{ padding: '0.75rem', border: '1px solid #e5e7eb', textAlign: 'left' }}>Plan</th>
                                <th style={{ padding: '0.75rem', border: '1px solid #e5e7eb', textAlign: 'left' }}>Status</th>
                                <th style={{ padding: '0.75rem', border: '1px solid #e5e7eb', textAlign: 'left' }}>Payment ID</th>
                              </tr>
                            </thead>
                            <tbody>
                              {selectedReferrerDetails.detailedPayments.map(payment => (
                                <tr key={payment.payment_id}>
                                  <td style={{ padding: '0.75rem', border: '1px solid #e5e7eb' }}>
                                    {formatDate(payment.created_at)}
                                  </td>
                                  <td style={{ padding: '0.75rem', border: '1px solid #e5e7eb', fontWeight: 'bold' }}>
                                    {formatCurrency(payment.amount)}
                                  </td>
                                  <td style={{ padding: '0.75rem', border: '1px solid #e5e7eb' }}>
                                    <span style={{
                                      padding: '2px 6px',
                                      borderRadius: '8px',
                                      fontSize: '0.8em',
                                      background: '#dbeafe',
                                      color: '#1d4ed8'
                                    }}>
                                      {payment.plan_id}
                                    </span>
                                  </td>
                                  <td style={{ padding: '0.75rem', border: '1px solid #e5e7eb' }}>
                                    <span style={{
                                      padding: '2px 6px',
                                      borderRadius: '8px',
                                      fontSize: '0.8em',
                                      fontWeight: 'bold',
                                      background: ['finished', 'early_confirmed', 'confirmed'].includes(payment.status) ? '#dcfce7' : '#fef3c7',
                                      color: ['finished', 'early_confirmed', 'confirmed'].includes(payment.status) ? '#166534' : '#92400e'
                                    }}>
                                      {payment.status}
                                    </span>
                                  </td>
                                  <td style={{ 
                                    padding: '0.75rem', 
                                    border: '1px solid #e5e7eb', 
                                    fontFamily: 'Monaco, Consolas, monospace',
                                    fontSize: '0.8rem',
                                    background: '#f8fafc'
                                  }}>
                                    {payment.payment_id}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </AnalyticsSection>
                    )}

                    {selectedReferrerDetails.detailedPayments && selectedReferrerDetails.detailedPayments.length === 0 && (
                      <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                        No payments found for this referrer.
                      </div>
                    )}
                  </>
                )}

                <ModalActions>
                  <ActionButton 
                    onClick={() => window.open(`/ref/${selectedReferrerDetails.id}`, '_blank')}
                    variant="secondary"
                  >
                    <FiExternalLink />
                    Open Referrer Panel
                  </ActionButton>
                  <ActionButton onClick={() => setShowDetailsModal(false)}>
                    Close
                  </ActionButton>
                </ModalActions>
              </ModalContent>
            </AnalyticsModal>
          )}
        </MainContent>
      </AdminContainer>
    </AdminLayout>
  );
};

export default ReferrersPage; 